import { useState } from 'react';
import { LABEL_PRESET_CODES, labelSizeSchema, type LabelPresetCode, type LabelSize } from '@eombor/contracts';

/**
 * ETIKETKA O'LCHAMI — brauzerda eslab qolinadi.
 *
 * Rulon omborga bir marta olinadi va yiliga bir marta almashadi, etiketka
 * esa kuniga bosiladi — har bosishda o'lchamni qaytadan tanlash bekorga
 * urinish bo'lardi. Server tomonda saqlash ham mumkin edi, lekin o'lcham
 * PRINTERGA bog'liq, foydalanuvchiga emas: ikkinchi kompyuterda boshqa
 * printer tursa, tanlov ham boshqa bo'lishi kerak.
 *
 * Tanlov IKKALA etiketka uchun bitta: katakniki ham, mahsulotniki ham
 * o'sha rulondan chiqadi. Ikki alohida xotira bo'lsa, skladchi bir kuni
 * ikkinchi oynada boshqa o'lcham turganini ko'rib, sababini topolmasdi.
 */
/**
 * Kalitda `v2` turibdi va u bir marta hammaning SAQLANGAN tanlovini
 * tashlab yuboradi.
 *
 * Sabab: eski tanlov `ROLL_105x80_2` PAYDO BO'LGUNCHA qilingan. Ya'ni
 * odam «bitta etiketka» ni tanlaganda, «ikkiga bo'lib» degan variant
 * ro'yxatda umuman yo'q edi — bu tanlov emas, yagona imkoniyat edi.
 * Uni hurmat qilib o'tirish har bir eski foydalanuvchini qog'ozni ikki
 * barobar ko'p sarflashga mahkum qilardi, sababi esa ko'rinmasdi:
 * mahsulot etiketkasi oynasida varaq tanlagichi yo'q va u JAVON
 * yorliqlari oynasidan boshqariladi.
 *
 * Keyingi safar tanlov haqiqatan ham tanlov bo'ladi va kalit
 * o'zgarmaydi.
 */
const PRESET_KEY = 'eombor.label.preset.v2';
const SIZE_KEY = 'eombor.label.size';

/**
 * JIMLIKDAGI varaq — bitta qog'ozga IKKITA etiketka.
 *
 * Tanlov qog'oz hisobidan: 105 × 80 rulonning yarmi (105 × 39,4) ga
 * etiketkaning hamma qismi sig'adi — katta partiya raqami, nom, katta
 * miqdor va katta QR. Ya'ni bitta etiketka bosishdan yutqazilmaydi,
 * rulon esa ikki barobar uzoqqa yetadi.
 *
 * Butun 100 × 80 ro'yxatda qoladi: baland quti yoki uzoqdan o'qiladigan
 * joy uchun u haligacha yaxshiroq.
 *
 * Bu JIMLIKDAGI qiymat, majburiy emas: odam o'zi boshqasini tanlasa,
 * tanlovi saqlanadi va bu yerdagi o'zgarish unga tegmaydi.
 */
export const DEFAULT_LABEL_PRESET: LabelPresetCode = 'ROLL_105x80_2';

export interface SizeDraft {
  widthMm: string;
  heightMm: string;
  perSheet: 1 | 2;
}

const EMPTY_SIZE: SizeDraft = { widthMm: '', heightMm: '', perSheet: 1 };

const readPreset = (fallback: LabelPresetCode): LabelPresetCode => {
  const saved = globalThis.localStorage?.getItem(PRESET_KEY);
  return LABEL_PRESET_CODES.includes(saved as LabelPresetCode) ? (saved as LabelPresetCode) : fallback;
};

const readSize = (): SizeDraft => {
  const saved = globalThis.localStorage?.getItem(SIZE_KEY);
  if (saved === null || saved === undefined) return EMPTY_SIZE;
  try {
    // Saqlangan qiymat foydalanuvchi tegishi mumkin bo'lgan joyda turadi —
    // buzilgan bo'lsa oyna ochilmay qolmasligi kerak, bo'sh maydon yetadi.
    const parsed = labelSizeSchema.safeParse(JSON.parse(saved));
    if (!parsed.success) return EMPTY_SIZE;
    return {
      widthMm: String(parsed.data.widthMm),
      heightMm: String(parsed.data.heightMm),
      perSheet: parsed.data.perSheet,
    };
  } catch {
    return EMPTY_SIZE;
  }
};

const parseSize = (draft: SizeDraft): ReturnType<typeof labelSizeSchema.safeParse> =>
  labelSizeSchema.safeParse({
    widthMm: Number(draft.widthMm),
    heightMm: Number(draft.heightMm),
    perSheet: draft.perSheet,
  });

export interface LabelSizeState {
  preset: LabelPresetCode;
  size: SizeDraft;
  /** `CUSTOM` tanlangan, lekin o'lcham to'liq emas — bosish mumkin emas. */
  sizeMissing: boolean;
  choosePreset: (next: LabelPresetCode) => void;
  changeSize: (patch: Partial<SizeDraft>) => void;
  /** So'rovga yoyiladigan qism: `CUSTOM` bo'lmasa bo'sh obyekt qaytadi. */
  sizeInput: () => { size?: LabelSize };
}

export const useLabelSize = (initial: LabelPresetCode = DEFAULT_LABEL_PRESET): LabelSizeState => {
  const [preset, setPreset] = useState<LabelPresetCode>(() => readPreset(initial));
  const [size, setSize] = useState(readSize);

  const custom = parseSize(size);

  return {
    preset,
    size,
    sizeMissing: preset === 'CUSTOM' && !custom.success,
    choosePreset: (next) => {
      setPreset(next);
      globalThis.localStorage?.setItem(PRESET_KEY, next);
    },
    changeSize: (patch) => {
      const next = { ...size, ...patch };
      setSize(next);
      // Faqat TO'G'RI o'lcham saqlanadi: yarim yozilgan son keyingi
      // ochilishda maydonni buzib ko'rsatardi.
      const valid = parseSize(next);
      if (valid.success) globalThis.localStorage?.setItem(SIZE_KEY, JSON.stringify(valid.data));
    },
    sizeInput: () => (preset === 'CUSTOM' && custom.success ? { size: custom.data } : {}),
  };
};
